import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  APPROVAL_RECORD_MODEL_CARD_QUOTA_ALLOCATION,
  APPROVAL_RECORD_MODEL_CARD_QUOTA_ALLOCATION_REQUEST,
  APPROVAL_RECORD_MODEL_CARD_QUOTA_REQUEST,
  ApprovalRequestDetail,
} from '../../../../../core/models/approval/approval-request.model';
import { CardQuotaRequestDetail } from '../../../../../core/models/card-quota/card-quota-request.model';
import { QuotaAllocationDetail, QuotaAllocationRequestDetail } from '../../../../../core/models/card-quota/client-quota.model';
import { MapperApprovalRequestService } from '../../../../../core/mappers/approval/mapper-approval-request.service';
import { MapperCardQuotaRequestService } from '../../../../../core/mappers/card-quota/mapper-card-quota-request.service';
import { MapperClientQuotaService } from '../../../../../core/mappers/card-quota/mapper-client-quota.service';
import { ApiApprovalRequestService } from '../../../../../core/services/api/approval/api-approval-request.service';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-approval-request',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-approval-request.component.html',
  styleUrl: './modal-approval-request.component.css',
})
export class ModalApprovalRequestComponent implements OnInit {
  @Input() requestId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() actionSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  detail: ApprovalRequestDetail | null = null;
  cardQuotaDetail: CardQuotaRequestDetail | null = null;
  allocationDetail: QuotaAllocationDetail | null = null;
  allocationRequestDetail: QuotaAllocationRequestDetail | null = null;
  remark = '';
  forwardUserId = 0;
  isLoading = signal(false);
  isSubmitting = signal(false);
  showApproveConfirm = signal(false);
  showRejectConfirm = signal(false);

  private readonly api = inject(ApiApprovalRequestService);
  private readonly cardQuotaApi = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperApprovalRequestService);
  private readonly cardQuotaMapper = inject(MapperCardQuotaRequestService);
  private readonly clientQuotaMapper = inject(MapperClientQuotaService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  ngOnInit(): void {
    if (this.requestId > 0) {
      this.loadDetail();
    }
  }

  get pendingStepId(): number | null {
    return this.detail ? this.mapper.getPendingStepId(this.detail) : null;
  }

  statusLabel = (status: string) => this.mapper.statusLabel(status);
  stepStatusLabel = (status: string) => this.mapper.stepStatusLabel(status);
  recordModelLabel = (model: string) => this.mapper.recordModelLabel(model);
  actionLabel = (action: string) => this.clientQuotaMapper.allocationActionLabel(action);
  requestStatusLabel = (status: string) => this.clientQuotaMapper.allocationRequestStatusLabel(status);
  requestTypeLabel = (type: string) => this.cardQuotaMapper.requestTypeLabel(type);

  onClose(): void {
    this.close.emit();
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString('th-TH');
  }

  openApproveConfirm(): void {
    const stepId = this.pendingStepId;
    const userId = this.resolveUserId();
    if (!stepId || !userId || !this.detail) {
      this.toast.error('ไม่พบขั้นตอนที่รออนุมัติ');
      return;
    }
    this.showApproveConfirm.set(true);
  }

  closeApproveConfirm(): void {
    this.showApproveConfirm.set(false);
  }

  confirmApprove(): void {
    const stepId = this.pendingStepId;
    const userId = this.resolveUserId();
    if (!stepId || !userId || !this.detail) {
      this.toast.error('ไม่พบขั้นตอนที่รออนุมัติ');
      return;
    }

    this.closeApproveConfirm();
    this.isSubmitting.set(true);
    this.api.approveRequestStep(stepId, { userId, remark: this.remark || null }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSubmitting.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'อนุมัติสำเร็จ');
          this.actionSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'อนุมัติไม่สำเร็จ');
        }
      },
      error: () => {
        this.isSubmitting.set(false);
        this.toast.error('เกิดข้อผิดพลาด');
      },
    });
  }

  openRejectConfirm(): void {
    if (!this.remark.trim()) {
      this.toast.error('กรุณาระบุเหตุผลการปฏิเสธ');
      return;
    }
    this.showRejectConfirm.set(true);
  }

  closeRejectConfirm(): void {
    this.showRejectConfirm.set(false);
  }

  confirmReject(): void {
    const stepId = this.pendingStepId;
    const userId = this.resolveUserId();
    if (!stepId || !userId) {
      this.toast.error('ไม่พบขั้นตอนที่รออนุมัติ');
      return;
    }
    if (!this.remark.trim()) {
      this.toast.error('กรุณาระบุเหตุผลการปฏิเสธ');
      return;
    }

    this.closeRejectConfirm();
    this.isSubmitting.set(true);
    this.api.rejectRequestStep(stepId, { userId, remark: this.remark.trim() }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSubmitting.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ปฏิเสธสำเร็จ');
          this.actionSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'ปฏิเสธไม่สำเร็จ');
        }
      },
      error: () => {
        this.isSubmitting.set(false);
        this.toast.error('เกิดข้อผิดพลาด');
      },
    });
  }

  onForward(): void {
    const userId = this.resolveUserId();
    if (!this.detail || !userId || !this.forwardUserId) {
      this.toast.error('กรุณาเลือกผู้อนุมัติชั้นถัดไป');
      return;
    }

    this.isSubmitting.set(true);
    this.api
      .forwardApprovalRequest(this.detail.id, {
        userId,
        assignedUserId: this.forwardUserId,
        remark: this.remark || null,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapActionResponse(res);
          this.isSubmitting.set(false);
          if (mapped.isSuccess) {
            this.toast.success(mapped.message || 'ส่งต่อสำเร็จ');
            this.actionSuccess.emit();
            this.onClose();
          } else {
            this.toast.error(mapped.message || 'ส่งต่อไม่สำเร็จ');
          }
        },
        error: () => {
          this.isSubmitting.set(false);
          this.toast.error('เกิดข้อผิดพลาด');
        },
      });
  }

  private loadDetail(): void {
    const userId = this.resolveUserId();
    if (!userId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้');
      return;
    }

    this.isLoading.set(true);
    this.api.getApprovalRequestById(this.requestId, userId).subscribe({
      next: (res) => {
        this.detail = this.mapper.mapDetail(res as ApprovalRequestDetail & Record<string, unknown>);
        const defaultCandidate = this.detail.forwardCandidates.find((c) => c.isDefault);
        this.forwardUserId = defaultCandidate?.userId ?? this.detail.forwardCandidates[0]?.userId ?? 0;
        this.loadLinkedRecord();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private loadLinkedRecord(): void {
    if (!this.detail?.recordId) return;

    if (this.detail.recordModel === APPROVAL_RECORD_MODEL_CARD_QUOTA_REQUEST) {
      this.cardQuotaApi.getCardQuotaRequestById(this.detail.recordId).subscribe({
        next: (res) => {
          this.cardQuotaDetail = this.cardQuotaMapper.mapDetail(res as CardQuotaRequestDetail & Record<string, unknown>);
        },
        error: () => {
          this.cardQuotaDetail = null;
        },
      });
      return;
    }

    if (this.detail.recordModel === APPROVAL_RECORD_MODEL_CARD_QUOTA_ALLOCATION_REQUEST) {
      this.cardQuotaApi.getQuotaAllocationRequestById(this.detail.recordId).subscribe({
        next: (res) => {
          this.allocationRequestDetail = this.clientQuotaMapper.mapAllocationRequestDetail(
            res as QuotaAllocationRequestDetail & Record<string, unknown>
          );
        },
        error: () => {
          this.allocationRequestDetail = null;
        },
      });
      return;
    }

    if (this.detail.recordModel === APPROVAL_RECORD_MODEL_CARD_QUOTA_ALLOCATION) {
      this.cardQuotaApi.getQuotaAllocationById(this.detail.recordId).subscribe({
        next: (res) => {
          this.allocationDetail = this.clientQuotaMapper.mapAllocationDetail(
            res as QuotaAllocationDetail & Record<string, unknown>
          );
        },
        error: () => {
          this.allocationDetail = null;
        },
      });
    }
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return this.requestId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
